/**
 * B638 (e) — a US ZIP's state, from its first three digits (option (i),
 * DEFAULT RECORDED 2026-10-06 by the design seat under Pathik's 2026-10-06
 * instruction; he overrules with "no on B638e"). Stripe's Payment Element
 * asks a US buyer for country + ZIP only, so a card's billing address
 * arrives with no state and "customers from California" had nothing to
 * read. The ZIP is reduced to its state here and dropped: no caller keeps
 * it (B350's coarse-geo wall — country, region, city, nothing finer).
 *
 * The USPS ZIP3 prefix ranges, bundled. Military prefixes (AA/AE/AP) and
 * unassigned ones answer undefined — honest absence, never a guess. A
 * handful of prefixes cross state lines (a ZIP3 is a sorting centre, not a
 * border); the prefix's own state is the answer, which is what a billing
 * map needs.
 */

/** [first prefix, last prefix, state] — inclusive, ascending, non-overlapping. */
const RANGES: readonly (readonly [number, number, string])[] = [
  [5, 5, "NY"], [6, 7, "PR"], [8, 8, "VI"], [9, 9, "PR"],
  [10, 27, "MA"], [28, 29, "RI"], [30, 38, "NH"], [39, 49, "ME"],
  [50, 54, "VT"], [55, 55, "MA"], [56, 59, "VT"], [60, 69, "CT"], [70, 89, "NJ"],
  [100, 149, "NY"], [150, 196, "PA"], [197, 199, "DE"],
  [200, 200, "DC"], [201, 201, "VA"], [202, 205, "DC"], [206, 219, "MD"],
  [220, 246, "VA"], [247, 268, "WV"], [270, 289, "NC"], [290, 299, "SC"],
  [300, 319, "GA"], [320, 339, "FL"], [341, 349, "FL"], [350, 369, "AL"],
  [370, 385, "TN"], [386, 397, "MS"], [398, 399, "GA"], [400, 427, "KY"],
  [430, 459, "OH"], [460, 479, "IN"], [480, 499, "MI"], [500, 528, "IA"],
  [530, 549, "WI"], [550, 567, "MN"], [569, 569, "DC"], [570, 577, "SD"],
  [580, 588, "ND"], [590, 599, "MT"], [600, 629, "IL"], [630, 658, "MO"],
  [660, 679, "KS"], [680, 693, "NE"], [700, 714, "LA"], [716, 729, "AR"],
  [730, 732, "OK"], [733, 733, "TX"], [734, 749, "OK"], [750, 799, "TX"],
  [800, 816, "CO"], [820, 831, "WY"], [832, 838, "ID"], [840, 847, "UT"],
  [850, 865, "AZ"], [870, 884, "NM"], [885, 885, "TX"], [889, 898, "NV"],
  [900, 961, "CA"], [967, 968, "HI"], [969, 969, "GU"], [970, 979, "OR"],
  [980, 994, "WA"], [995, 999, "AK"],
];

/** Every code usStateOfZip can answer: the 50 states, DC, and the three territories the table names. */
export const US_STATE_CODES: readonly string[] = Object.freeze([...new Set(RANGES.map((r) => r[2]))].sort());

const ZIP = /^(\d{3})\d{2}(-\d{4})?$/;

/** A five-digit ZIP (or ZIP+4) → its two-letter state; anything else, or a prefix with no state, → undefined. */
export function usStateOfZip(zip: string): string | undefined {
  const m = ZIP.exec(zip);
  if (m === null) return undefined;
  const prefix = Number(m[1]);
  for (const [lo, hi, state] of RANGES) {
    if (prefix < lo) return undefined;
    if (prefix <= hi) return state;
  }
  return undefined;
}
